import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, api } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Activity, Eye, EyeOff, ArrowLeft, CheckCircle } from 'lucide-react';
import { FigIcon } from '../components/FigIcon';
import { toast } from 'sonner';

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    consent_data_processing: true,
    consent_marketing: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasPendingQuestionnaire, setHasPendingQuestionnaire] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Check if there's a pending questionnaire
    const pending = localStorage.getItem('fig_pending_questionnaire');
    setHasPendingQuestionnaire(!!pending);
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (!formData.consent_data_processing) {
      toast.error('You must agree to data processing to continue');
      return;
    }

    setLoading(true);
    try {
      await register({
        email: formData.email,
        password: formData.password,
        name: formData.name || null,
        consent_data_processing: formData.consent_data_processing,
        consent_marketing: formData.consent_marketing
      });
      
      // Check for pending questionnaire
      const pendingQuestionnaire = localStorage.getItem('fig_pending_questionnaire');
      const redirect = searchParams.get('redirect');
      
      if (redirect === 'submit-questionnaire' && pendingQuestionnaire) {
        // Submit the pending questionnaire
        try {
          const questionnaireData = JSON.parse(pendingQuestionnaire);
          await api.post('/questionnaire/submit', questionnaireData);
          localStorage.removeItem('fig_pending_questionnaire');
          localStorage.removeItem('trajectory_questionnaire_v2');
          toast.success('Account created and assessment saved!');
          navigate('/results');
        } catch (qError) {
          toast.error('Account created but failed to save assessment. Please try again.');
          navigate('/questionnaire');
        }
      } else {
        toast.success('Account created successfully');
        const pkg = searchParams.get('package');
        if (redirect === 'checkout' && pkg) {
          navigate(`/checkout?package=${pkg}`);
        } else {
          navigate('/questionnaire');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#E8E4DC] flex">
      {/* Left side - Form */}
      <div className="flex-1 flex flex-col justify-center px-8 lg:px-16 py-12">
        <div className="max-w-md w-full mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-[#525252] hover:text-[#1A1C19] mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
          
          <div className="flex items-center gap-2 mb-8">
            <FigIcon className="w-8 h-8 text-[#2F3E30]" />
            <span className="font-bold text-2xl tracking-tight" style={{ fontFamily: 'Libre Baskerville, serif' }}>FIG HEALTH</span>
          </div>

          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Libre Baskerville, serif' }}>CREATE ACCOUNT</h1>
          <p className="text-[#525252] mb-6">
            {hasPendingQuestionnaire 
              ? 'Create an account to save your assessment and see your results'
              : 'Start your metabolic assessment'
            }
          </p>

          {hasPendingQuestionnaire && (
            <div className="bg-[#2F3E30]/10 border border-[#2F3E30]/20 p-4 mb-6 flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-[#2F3E30] flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-[#1A1C19] text-sm">Assessment Complete</p>
                <p className="text-[#525252] text-sm">Your responses have been saved. Create an account to view your results.</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="name" className="text-sm font-medium">Name (optional)</Label>
              <Input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                placeholder="John"
                className="mt-2 bg-[#EBEBE6] border-[#D1D1CD] h-12 focus:border-[#2F3E30]"
                data-testid="register-name-input"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-sm font-medium">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                className="mt-2 bg-[#EBEBE6] border-[#D1D1CD] h-12 focus:border-[#2F3E30]"
                data-testid="register-email-input"
                required
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-medium">Password</Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="bg-[#EBEBE6] border-[#D1D1CD] h-12 pr-10 focus:border-[#2F3E30]"
                  data-testid="register-password-input"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#525252] hover:text-[#1A1C19]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="mt-1 text-xs text-[#525252]">Minimum 8 characters</p>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-sm font-medium">Confirm Password</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="••••••••"
                className="mt-2 bg-[#EBEBE6] border-[#D1D1CD] h-12 focus:border-[#2F3E30]"
                data-testid="register-confirm-password-input"
                required
              />
            </div>

            <div className="space-y-3 pt-2">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="consent_data_processing"
                  name="consent_data_processing"
                  checked={formData.consent_data_processing}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, consent_data_processing: checked }))}
                  className="mt-1"
                  data-testid="consent-data-checkbox"
                />
                <Label htmlFor="consent_data_processing" className="text-sm text-[#525252] leading-relaxed cursor-pointer">
                  I agree to the processing of my data as described in the <Link to="/privacy" className="text-[#2F3E30] hover:underline">Privacy Policy</Link> *
                </Label>
              </div>
              <div className="flex items-start gap-3">
                <Checkbox
                  id="consent_marketing"
                  name="consent_marketing"
                  checked={formData.consent_marketing}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, consent_marketing: checked }))}
                  className="mt-1"
                  data-testid="consent-marketing-checkbox"
                />
                <Label htmlFor="consent_marketing" className="text-sm text-[#525252] leading-relaxed cursor-pointer">
                  I'd like to receive occasional updates about metabolic health
                </Label>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 bg-[#2F3E30] hover:bg-[#3A4D3B] text-white font-medium"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>

          <p className="mt-8 text-center text-sm text-[#525252]">
            Already have an account?{' '}
            <Link to="/login" className="text-[#2F3E30] hover:underline" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img 
          src="https://images.unsplash.com/photo-1711466093648-1adfe1664678?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Trail running"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#E8E4DC] to-transparent" />
        <div className="absolute inset-0 bg-[#E8E4DC]/30" />
      </div>
    </div>
  );
};

export default RegisterPage;
